package dev.lingvo.site.ds

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.scrollBehavior
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.loadFromLocalStorage
import com.varabyte.kobweb.silk.theme.colors.systemPreference
import com.varabyte.kobweb.silk.theme.colors.cssClass
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.link
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.StylePropertyValue
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.AnimationTimingFunction
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import kotlin.math.roundToInt
import org.w3c.dom.HTMLElement
import kotlinx.browser.document

class SitePalette(
    val surfaceBase: Color,
    val surface: Color,
    val surfaceRaised: Color,
    val surfaceSunken: Color,
    val border: Color,
    val borderStrong: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textMuted: Color,
    val accent: Color,
    val accentHover: Color,
    val accentSubtle: Color,
    val accentContrast: Color,
    val success: Color,
    val warning: Color,
    val danger: Color,
    val info: Color,
)

/**
 * Polyglot, in two modes.
 *
 * Night is not a second theme — its ground *is* `p-ink`, so the product reads as the same brand
 * after dark rather than as a second language. Neither is Night an inversion of Day: a hue that
 * reads correctly on cream is either invisible or garish on ink, so each member is chosen against
 * its own ground and measured there.
 *
 * Every member below carries its measured contrast against the grounds it can appear on. They are
 * computed, not estimated, and three of them are here because the values they replace failed:
 *
 *  - `borderStrong` was 1.46:1 in light and 1.60:1 in dark. `LTextField` draws every input in the
 *    product with it, so **every input had an illegal boundary in both modes** (WCAG 1.4.11 wants
 *    3:1). It is now 3.67 / 3.93 on base.
 *  - `textMuted` was 3.42:1 in light — below the 4.5:1 floor for the 59 sites that carry
 *    timestamps, locale codes and the machine-provenance label. Now 5.21 / 6.16.
 *  - `success` as published in Polyglot was 4.39:1 on the zebra ground, and "approved green on a
 *    zebra row" is one of the highest-frequency pairings in the product. Darkened one step to 4.82.
 *
 * Both instances use named arguments against a class with no defaults, so a member added here is a
 * compile error at exactly two sites. That is deliberate: it is what stops Night rotting into a
 * second-class theme until dropping it "looks justified".
 */
object SitePalettes {
    /** Ground is `p-ink`. Measured against surfaceBase / surface / surfaceRaised. */
    val dark = SitePalette(
        surfaceBase = Color.rgb(0x14120F),
        surface = Color.rgb(0x1C1A16),
        surfaceRaised = Color.rgb(0x262320),
        surfaceSunken = Color.rgb(0x0B0A08),
        border = Color.rgb(0x33302A),
        borderStrong = Color.rgb(0x7A7264), // 3.93 / 3.66 / 3.29
        textPrimary = Color.rgb(0xF6F3EC), // 16.87 / 15.68 / 14.10
        textSecondary = Color.rgb(0xB8B0A2), // 8.70 / 8.08 / 7.27
        textMuted = Color.rgb(0x9C9384), // 6.16 / 5.73 / 5.15
        accent = Color.rgb(0x8B7CFF), // 5.72 / 5.32 / 4.78
        accentHover = Color.rgb(0xA79BFF),
        accentSubtle = Color.rgb(0x221E3A),
        // Inverts by mode, which is exactly what this token is for: ink on the night accent is
        // 5.72, paper on it is 2.95 and fails.
        accentContrast = Color.rgb(0x14120F),
        success = Color.rgb(0x3DBE8B), // 7.96
        warning = Color.rgb(0xE3A94F), // 8.95
        danger = Color.rgb(0xFF6B6B), // 6.74
        info = Color.rgb(0x7FA9FF), // 8.03
    )

    /**
     * Ground is `p-paper` — the same paper the marketing site uses, so the `/app` seam has no
     * colour change at all.
     *
     * The fill ladder exists so wells read as recessed, not so cards read as raised: on a light
     * ground elevation is a hairline, never a fill. `surface` is never pure white; `surfaceRaised`
     * is, and is reserved for things that genuinely float. The previous light palette had
     * `surface` and `surfaceRaised` both `#FFFFFF` — literally zero separation, a bug that
     * survived because light mode had never once been screenshotted.
     */
    val light = SitePalette(
        surfaceBase = Color.rgb(0xF6F3EC),
        surface = Color.rgb(0xFDFBF7),
        surfaceRaised = Color.rgb(0xFFFFFF),
        surfaceSunken = Color.rgb(0xEDE8DD),
        border = Color.rgb(0xDCD5C6), // 1.32 — a divider, never a control boundary
        borderStrong = Color.rgb(0x857D6F), // 3.67 / 3.33 / 4.07
        textPrimary = Color.rgb(0x14120F), // 16.87 / 15.30 / 18.70
        textSecondary = Color.rgb(0x5C554A), // 6.64 / 6.02 / 7.36
        textMuted = Color.rgb(0x6B655A), // 5.21 / 4.73 / 5.78
        accent = Color.rgb(0x4436F0), // 6.25 / 5.67 / 6.93
        accentHover = Color.rgb(0x3C2FD4),
        accentSubtle = Color.rgb(0xE4E1FD), // accent on it 5.44, ink on it 14.68
        accentContrast = Color.rgb(0xFFFFFF), // on the accent fill: 6.93
        success = Color.rgb(0x137255), // 5.31 / 4.82 / 5.89
        warning = Color.rgb(0x8A5300), // 5.71 / 5.18 / 6.33
        danger = Color.rgb(0xBF2E2E), // 5.20 / 4.71 / 5.76
        info = Color.rgb(0x1F6390), // 5.84 / 5.30 / 6.47
    )
}

/**
 * Project identity is neither product action nor operational status.
 *
 * These tones are deliberately separate from [SitePalette.accent], [SitePalette.info],
 * [SitePalette.success], [SitePalette.warning], and [SitePalette.danger]. A project can be cobalt,
 * jade, amber, coral, or violet without implying selection, information, success, warning, or
 * failure. Each foreground clears 4.5:1 against its own 12% tint on [SitePalette.surface]; the
 * paired test measures every mode/accent combination so this boundary cannot silently regress.
 */
class ProjectIdentityPalette(
    val ink: Color,
    val cobalt: Color,
    val jade: Color,
    val amber: Color,
    val coral: Color,
    val violet: Color,
)

object ProjectIdentityPalettes {
    val dark = ProjectIdentityPalette(
        ink = Color.rgb(0xD7CFC2),
        cobalt = Color.rgb(0x91A9EE),
        jade = Color.rgb(0x78B9A2),
        amber = Color.rgb(0xD3AA6A),
        coral = Color.rgb(0xE38E83),
        violet = Color.rgb(0xB49ADD),
    )

    val light = ProjectIdentityPalette(
        ink = Color.rgb(0x352F28),
        cobalt = Color.rgb(0x2C4EA3),
        jade = Color.rgb(0x236B5A),
        amber = Color.rgb(0x7B5420),
        coral = Color.rgb(0x9A413C),
        violet = Color.rgb(0x68469A),
    )
}

fun ColorMode.toSitePalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> SitePalettes.light
    ColorMode.DARK -> SitePalettes.dark
}

fun ColorMode.toProjectIdentityPalette(): ProjectIdentityPalette = when (this) {
    ColorMode.LIGHT -> ProjectIdentityPalettes.light
    ColorMode.DARK -> ProjectIdentityPalettes.dark
}

/**
 * The palette whose ground an ink band is actually painted on.
 *
 * Ink bands use the page palette's [SitePalette.textPrimary] as their ground, so their foreground
 * must come from the palette tuned for that ground rather than from the page mode's palette.
 */
fun ColorMode.onInkPalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> SitePalettes.dark
    ColorMode.DARK -> SitePalettes.light
}

/**
 * A status tone, washed into an **opaque** tint you can safely set its own text on.
 *
 * A translucent fill picks up whatever it lands on, so the same badge is a different colour on a
 * card, on the page and on a zebra row — and its contrast changes with it. Under Polyglot Day that
 * is not academic: `danger` clears only 4.71:1 on `surfaceSunken` to begin with, so a 12% wash of
 * itself underneath its own text lands at **3.96:1**, below the 4.5 floor, on exactly the rows
 * where badges are commonest. Lowering the alpha does not rescue it — even 6% measures 4.33.
 *
 * Compositing against a fixed [SitePalette.surface] instead makes the tint one known colour
 * wherever it appears, and the worst tone-on-tint pair in either mode is then 4.63:1.
 *
 * Use for a non-interactive tinted ground — a badge, an alert, a well. Never for a control fill.
 */
/**
 * Binds each script face to the language of the text, not to a position in a fallback list.
 *
 * One rule per script, each prepending its face to the Latin stack so Latin inside a Japanese
 * string still sets in Instrument Sans. Applies to `[lang]` elements and their descendants, so
 * marking the editor's source and target panes is enough to cover the text inside them.
 *
 * Nothing here fires without a `lang` attribute — which is the point. Untagged text keeps today's
 * behaviour exactly.
 */
private fun registerScriptFonts(ctx: InitSilkContext) {
    SCRIPT_FONTS.forEach { (tag, face) ->
        ctx.stylesheet.registerStyleBase(":lang($tag)") {
            Modifier.fontFamily(face, *FontStack.Body)
        }
    }
}

fun Color.tintOn(ground: Color, alpha: Float): Color {
    val fg = this.toRgb()
    val bg = ground.toRgb()
    fun mix(f: Int, b: Int) = (f * alpha + b * (1f - alpha)).roundToInt().coerceIn(0, 255)
    return Color.rgb(mix(fg.red, bg.red), mix(fg.green, bg.green), mix(fg.blue, bg.blue))
}

/**
 * The space scale: **4 · 8 · 12 · 16 · 24 · 32 · 48 · 72**.
 *
 * Eleven names now carry eight values, because Polyglot's steps land on top of each other:
 * `XXS`==`XS` (4), `XL`==`XXL` (24), `Huge`==`Section` (48). That is deliberate here and
 * deliberately *not* cleaned up in this commit — collapsing the names means editing 817 call
 * sites, and a rename diff mixed into a geometry diff is a diff nobody can review. The values are
 * the contract; the surplus names are follow-up work.
 *
 * Roughly 60 hardcoded px values bypass this object entirely (`14.px` x14, `18.px` x11). Also
 * left alone, for the same reason.
 */
object Space {
    /** 2 -> 4. Badge and chip insets; 28 sites, each gaining a pixel per side. */
    val XXS = 4.px
    val XS = 4.px
    val SM = 8.px
    val MD = 12.px
    val LG = 16.px
    /** 20 -> 24. The loudest change in this commit: card and section padding across 56 files. */
    val XL = 24.px
    val XXL = 24.px
    val XXXL = 32.px
    val Huge = 48.px
    /** 56 -> 48. Section and Huge meet; the ladder keeps its rungs, the names do not. */
    val Section = 48.px
    val Hero = 72.px

    /**
     * The marketing page's gutter, and the only value here that is not a step on the ladder.
     *
     * 92 is a page-layout measure rather than a spacing step: it sets the column that every band on
     * the landing page shares, so 1440 - 2 x 92 = 1256 of content. Spacing *inside* a band still
     * comes from the eight steps above. `lingvo.pen` carries it as its own `space-band` variable for
     * the same reason — keeping it out of the ladder is what stops it being reached for as padding.
     */
    val Gutter = 92.px

    /**
     * How wide the content column is allowed to get, and the partner to [Gutter].
     *
     * **1256 = 1440 − 2 × 92.** `lingvo.pen` draws every marketing frame at 1440 with a 92 gutter
     * and defines no width token, because it never draws a viewport wider than the one it was drawn
     * at. The gutter alone is therefore only half a rule: it is exact at 1440 and says nothing about
     * a 2560px monitor, where 92px of padding leaves a 2376px measure and the bands run edge to edge
     * while the header sits in a column.
     *
     * Taking the design's own arithmetic gives both: at 1440 the column is exactly what the pen
     * drew, and beyond it the page stops growing. Change one of these two and the other has to move
     * with it, which is why they live together.
     *
     * This replaces a bare `76.cssRem` that was repeated in three places — the header, the footer
     * and the marketing page column — and agreed with the design at none of them.
     */
    val ContentMax = 1256.px

    /**
     * The full page measure, gutters included: **1440 = 1256 + 2 × 92**, which is the width every
     * frame in `lingvo.pen` is drawn at.
     *
     * For elements that carry the gutter themselves — the hero and the locale strip set their own
     * padding rather than going through `Band` — capping the outer box at this leaves exactly
     * [ContentMax] of content inside. It exists as a named value because the alternative was a CSS
     * `calc()` built by string interpolation, and the first version of that shipped the literal text
     * `calc(${'$'}{Space.ContentMax} + …)` into the stylesheet, where the browser dropped it as
     * invalid and the hero ran edge to edge with no warning anywhere.
     */
    val PageMax = 1440.px
}

/**
 * Four radii, and one rule about which is a button.
 *
 * **Anything you press is [Control]. [Pill] is for things that tell you something.** Badges, chips,
 * tags, avatars, switch tracks, spinners and progress bars are pills; buttons and links styled as
 * buttons are not. The shape is doing work — it separates "press this" from "read this" — and a
 * pill-shaped button spends that distinction for a stylistic flourish.
 *
 * This had drifted. `Start free` rendered at [Control] in the header and the hero and at [Pill] in
 * the closing band: one action, one page, two shapes. `lingvo.pen` cannot arbitrate it, because the
 * design file disagrees with itself — its `Button` and `ButtonSecondary` components are pills while
 * `ButtonOAuth` is [Control], and its frames use fourteen distinct radius values against the four
 * defined here.
 *
 * The one deliberate exception is a stepper: the −/+ controls on the plurals instrument are 40px
 * squares at [Pill], i.e. circles. A circular stepper is a different control type rather than a
 * button at a different radius, and the design file draws it that way too.
 */
object Radius {
    val Control = 8.px
    val Card = 12.px
    val Modal = 16.px
    val Pill = 999.px
}

/**
 * Three control heights, and the one place `40px` is actually named.
 *
 * Before this, `40` was written by hand at every field, select and segmented button in the
 * product — six places at last count — and [HIT_AREA_FLOOR_PX] carried the same number under a
 * different name for a different reason (the minimum pointer target, not a control's own height).
 * The two concepts collide at exactly one value today; [HIT_AREA_FLOOR_PX] is now defined from
 * [MD] so that collision is a fact about the design, not two constants that happen to agree.
 */
object ControlHeight {
    val SM = 32.px
    val MD = 40.px
    val LG = 48.px
}

/**
 * Polyglot's steps: **10.5 · 13.5 · 15 · 16.5 · 21 · 33 · 44 · 52**.
 *
 * **In `px`, not `cssRem`, and that is not a style choice.** At a 16px root, 16.5 / 13.5 / 10.5 are
 * 1.03125 / 0.84375 / 0.65625rem, and fractional rem produces fractional line boxes. In a product
 * that renders 200-row tabular tables, that drifts row height by a pixel across browsers and the
 * rows stop aligning with their own header.
 */
object TypeScale {
    val Micro = 10.5.px
    /** The heaviest step in the product: 174 call sites. */
    val Small = 13.5.px
    val BodySmall = 15.px
    val Body = 16.5.px

    /**
     * Sustained bilingual reading — the editor's source and target panes.
     *
     * New, and it exists because Polyglot collapses `Lead` (18) and `HeadingSmall` (22) onto the
     * same 21px step. Rather than let a reading size and a heading size share a token, the four
     * editor styles that were on `Lead` move here. 18px was already at the small end for reading
     * two languages side by side and is plainly too small for CJK, where the discriminating
     * detail fills the em box — so this is larger, not a compromise.
     */
    val Reading = 21.px

    /** Marketing only. The app reads at [Reading]; this is the landing page's to set. */
    val Lead = 18.px
    val HeadingSmall = 21.px
    val Heading = 33.px
    val Display = 44.px
    val Hero = 52.px

    /**
     * 88px, for the landing page's script wall and nothing else.
     *
     * A step above [Hero] because the wall is not a heading — it is nine words in nine scripts doing
     * the page's whole argument at once, and at 52px it reads as a headline that happens to be
     * multilingual rather than as a wall. Named for its single use so it does not become "the big
     * one" and drift onto section headings.
     */
    val Wall = 88.px
    const val BodyLineHeight = 1.5
    const val DisplayLineHeight = 1.15
    val MicroTracking = 0.08.em
}

/**
 * The Latin stacks, and deliberately nothing else.
 *
 * **No CJK family is appended to [Body], and that omission is the whole point.** Han is unified in
 * Unicode: 直, 骨, 今 and 門 are one codepoint each but are drawn differently in Japanese, Korean,
 * Simplified and Traditional Chinese. Append `Noto Sans JP, Noto Sans KR, Noto Sans SC` to a single
 * stack and the first one wins for every Han character in the product, permanently — a Chinese
 * reviewer opening their own queue would read Japanese glyph forms as a rendering defect, and no
 * screenshot an English-speaking team ever takes would show it.
 *
 * The script faces are bound by [registerScriptFonts] through `:lang()` instead, so the browser
 * picks the face from the language the text is actually in. That makes the `lang` attribute
 * load-bearing rather than decorative: text with no `lang` gets the Latin stack and the OS default
 * for Han, which is exactly today's behaviour and is the safe fallback.
 */
object FontStack {
    val Display = arrayOf("Bricolage Grotesque", "Instrument Sans", "sans-serif")
    val Body = arrayOf("Instrument Sans", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "sans-serif")
    val Mono = arrayOf("JetBrains Mono", "SFMono-Regular", "Consolas", "monospace")
}

/**
 * Per-script faces, selected by the language of the text rather than by stack order.
 *
 * Keyed on BCP-47 prefixes as `:lang()` matches them — `:lang(zh)` matches `zh-Hans`, `zh-CN` and
 * bare `zh`, so the Simplified and Traditional rules are listed after it and win by source order.
 * `:lang()` pseudo-classes all carry the same specificity, so later genuinely does win; the order
 * of this list is load-bearing and not alphabetical.
 *
 * **`zh-Hant` alone was unreachable.** The locale picker offers `zh`, `zh-CN`, `zh-HK` and `zh-TW`
 * and never emits `zh-Hant`, so the Traditional face was downloaded on every page that asked for a
 * script stylesheet and then selected by nothing. Hong Kong and Taiwan got Noto Sans SC —
 * Simplified glyph forms for Traditional text, which renders cleanly and is simply the wrong shape
 * of several hundred characters. `zh-Hant` stays because the API and CLI accept a script subtag
 * directly; the two region tags are what the product actually produces.
 */
private val SCRIPT_FONTS = listOf(
    "ja" to "Noto Sans JP",
    "ko" to "Noto Sans KR",
    "zh" to "Noto Sans SC",
    "zh-Hant" to "Noto Sans TC",
    "zh-TW" to "Noto Sans TC",
    "zh-HK" to "Noto Sans TC",
    "ar" to "Noto Sans Arabic",
    "he" to "Noto Sans Hebrew",
    "hi" to "Noto Sans Devanagari",
    "th" to "Noto Sans Thai",
    // Cyrillic and Greek. This was assumed to be covered by the Latin body stack; it is not.
    // Checked against the Google Fonts API: Instrument Sans serves `latin` and `latin-ext` only, and
    // Bricolage Grotesque `latin`, `latin-ext` and `vietnamese`. Neither carries a Cyrillic or Greek
    // range, so `Привет` and `Γεια` fell through to whatever the browser picked — most visibly in the
    // landing page's script wall, where they set at 88px beside eight words that did have a face.
    "ru" to "Noto Sans",
    "uk" to "Noto Sans",
    "bg" to "Noto Sans",
    "sr" to "Noto Sans",
    "mk" to "Noto Sans",
    "el" to "Noto Sans",
)

object ColorTokens {
    val SurfaceBase by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Surface by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val SurfaceRaised by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val SurfaceSunken by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Border by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val BorderStrong by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val TextPrimary by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val TextSecondary by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val TextMuted by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Accent by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val AccentHover by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val AccentSubtle by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val AccentContrast by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Success by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Warning by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Danger by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Info by StyleVariable<CSSColorValue>(prefix = "lingvo")
}

/**
 * A colour that paints nothing, for reserving space a colour will later occupy.
 *
 * Almost always a border: a control that grows a 1px outline on hover or selection must already own
 * that pixel, or gaining it shifts the control and everything beside it. Painting the border
 * transparent reserves the box without drawing anything.
 *
 * Here because it had been declared privately in three separate files — `AppTrail`, `SectionNav`
 * and `ModuleCards` — while a fourth spelled the same idea `Color.rgba(0, 0, 0, 0f)`. None of them
 * is wrong; four of them is how a codebase ends up unable to answer "what do we call nothing".
 *
 * Not a [ColorTokens] entry: those are CSS custom properties that change with the theme, and this
 * one is the same in every theme there will ever be.
 */
val Transparent: CSSColorValue = org.jetbrains.compose.web.css.Color("transparent")

/**
 * The dim behind a modal, and behind the editor's bottom sheet.
 *
 * Variables rather than an `rgba()` literal at each call site, because a scrim is a property of
 * the ground it dims: the same black at the same alpha is a veil over paper and very nearly
 * nothing over ink. Both are registered per mode so a light ground can ask for its own value
 * without hunting call sites.
 *
 * [Modal] and [Sheet] are separate because they are separately chosen — 0.62 behind a dialog that
 * owns the screen, 0.54 behind a sheet you can still see the field through. Today both modes get
 * the same value each, which is exactly what shipped; collapsing them into one variable would be
 * a rendering change wearing a refactor's clothes.
 */
object ScrimVars {
    val Modal by StyleVariable<CSSColorValue>(prefix = "lingvo")
    val Sheet by StyleVariable<CSSColorValue>(prefix = "lingvo")
}

/**
 * Depth, as two named layers rather than a shadow written at each call site.
 *
 * `docs/DESIGN.md` §"Space, shape, depth" states the rule this object exists to hold: **1px borders
 * do the work, and shadows appear only on floating layers.** A card is a hairline. A menu, a
 * tooltip, a toast and a modal have left the page behind and need to say so.
 *
 * The two values that document quotes are the dark ones — `0 1px 2px rgba(0,0,0,.24)` and
 * `0 8px 24px -6px rgba(0,0,0,.4)` — and they are two halves of one shadow, not alternatives: the
 * tight one is contact, the wide one is ambient. [Floating] draws both, which is the shape the
 * document describes.
 *
 * **Light is not the same shadow at the same strength.** DESIGN.md says "much lighter in light
 * mode" and the palette says why: on ink, a shadow is the only thing separating `surfaceRaised`
 * from the ground, while on paper `surfaceRaised` is pure white against cream and is already
 * separated before any shadow is drawn. 40% black over paper reads as dirt, not depth. Light
 * therefore gets roughly a third of the alpha, tinted with `p-ink` rather than pure black so the
 * shadow stays warm on a warm ground — a neutral grey shadow on cream is the single most reliable
 * way to make a light theme look like a different product.
 *
 * **Two rungs, not four.** [Floating] is applied to `LMenuStyle`, `LModalStyle`, `LToastStyle` and
 * `LTooltipStyle` — a dialog is the only thing above a scrim that already darkens everything behind
 * it, so it draws the same shadow a menu does rather than a heavier one that would only muddy the
 * scrim at its edge — and [Sheet] draws the editor's assist sheet upward instead of the hand-written
 * `box-shadow` it used to carry. Cards stay hairline by policy and have no rung. Two other levels
 * were declared alongside these and are gone: `Raised`, the contact shadow alone, had no call site
 * to begin with, and `Overlay`, a rung meant to out-rank something already floating, lost its only
 * candidate consumer when `LModalStyle` explicitly declined it for the reason above — a shadow with
 * no consumer is the same debt either way this codebase has already removed a motion token for.
 *
 * Read through [elevation]; never spell a `box-shadow` at a call site.
 */
object Elevation {
    /** Contact and ambient. Menus, tooltips, toasts and dialogs — anything that floats over the content. */
    val Floating by StyleVariable<StylePropertyValue>(prefix = "lingvo-elevation")

    /**
     * The same ambient shadow, thrown **upwards**, for a surface anchored to the bottom edge.
     *
     * [Floating] lights the page from above, which is right for a menu, a tooltip or a dialog: each
     * of them floats somewhere in the middle of the viewport and casts down onto the content it
     * covers. A bottom sheet does not. It is welded to the bottom of the window, so a downward
     * shadow falls off the screen entirely and the sheet meets the page it is covering with no seam
     * at all — which is exactly what the editor's assist sheet was fixing when it hand-wrote
     * `0 -12px 42px -12px rgba(0,0,0,.58)` into `EditorAssistStyles.kt`.
     *
     * That value is the dark one below, unchanged: this rung exists so the one surface that needed
     * an upward shadow stops being the one surface that spells a `box-shadow` at its call site, and
     * so the next sheet does not invent a second number. Light gets the same geometry at `p-ink` and
     * a third of the alpha, for the reason [Floating] does.
     */
    val Sheet by StyleVariable<StylePropertyValue>(prefix = "lingvo-elevation")
}

/** The two shadows of one colour mode, in the order [Elevation] declares them. */
private class ElevationShadows(
    val floating: String,
    val sheet: String,
)

/**
 * Both modes, side by side, so the light values can be read against the dark ones they answer.
 *
 * The dark pair is DESIGN.md's, verbatim. Light keeps the same geometry — the same offsets, blurs
 * and spreads — and changes only the ink, because a shadow that also changes shape between modes
 * stops being one design decision and becomes two.
 */
private object ElevationValues {
    val dark = ElevationShadows(
        floating = "0 1px 2px rgba(0, 0, 0, 0.24), 0 8px 24px -6px rgba(0, 0, 0, 0.4)",
        // `EditorAssistStyles.kt`'s hand-written sheet shadow, verbatim, so adopting the token
        // cannot change what that surface has always drawn.
        sheet = "0 -12px 42px -12px rgba(0, 0, 0, 0.58)",
    )

    /** `rgba(20, 18, 15, …)` is `p-ink` (`#14120F`), the ground the dark mode paints on. */
    val light = ElevationShadows(
        floating = "0 1px 2px rgba(20, 18, 15, 0.08), 0 8px 24px -6px rgba(20, 18, 15, 0.14)",
        sheet = "0 -12px 42px -12px rgba(20, 18, 15, 0.20)",
    )
}

private fun ColorMode.toElevationShadows(): ElevationShadows =
    if (isDark) ElevationValues.dark else ElevationValues.light

/**
 * Draw [level]'s shadow.
 *
 * The variable is resolved by the browser, not by Kotlin, so a surface written once picks up the
 * right shadow when the colour mode changes underneath it — no `ColorMode.current` read, and no
 * recomposition, for a property that is purely paint.
 */
fun Modifier.elevation(level: StyleVariable.PropertyValue<StylePropertyValue>): Modifier =
    styleModifier { property("box-shadow", "${level.value()}") }

/**
 * Motion tokens.
 *
 * Three rules keep every animation in the product feeling like one system:
 *
 * 1. **Duration follows distance, not importance.** A colour change is [Fast]; something that
 *    changes size or position is [Standard]; something that covers the screen is [Slow].
 * 2. **Easing follows direction.** Entering or responding to input uses [EaseOut]; leaving uses
 *    [EaseIn]; moving between two resting states uses [EaseInOut]. [EaseEmphasized] is reserved for
 *    the single orchestrated moment on the landing page.
 * 3. **Anything that competes with typing does not animate.** Row selection in the editor, caret
 *    movement and list filtering are instant — a 120ms fade there reads as lag, not polish.
 *
 * Reach for the [transitionColors] / [transitionSurface] / [transition] modifiers rather than
 * writing a `transition` string by hand, so a change here reaches the whole product. The global
 * `prefers-reduced-motion: reduce` rule in [initLingvoTheme] neutralises all of it.
 */
object Motion {
    /** No animation. Use where responsiveness matters more than continuity. */
    const val InstantMs = 0

    /** 120ms — hover, focus, and other in-place colour or opacity changes. */
    const val FastMs = 120

    /** 180ms — press feedback, expand/collapse, menus, anything that moves or resizes. */
    const val StandardMs = 180

    /** 240ms — overlays, modals and bottom sheets covering existing content. */
    const val SlowMs = 240

    /** 320ms — the landing hero's glyph mosaic. The only place this is allowed. */
    const val DeliberateMs = 320

    /**
     * 380ms — one line of the landing page's script wall arriving. Load only, once per page.
     *
     * The whole self-starting motion budget for the marketing page is three of these: three lines,
     * and nothing else on that page moves unless the reader asks it to. Any further entrance has to
     * be a response to something they did.
     *
     * They *do* overlap, and that is the design rather than a slip. `ScriptWall` offsets each line
     * by 105ms, so all three finish at 590ms instead of 1,140ms and read as one movement with depth
     * rather than three things taking turns. This paragraph previously said "and they do not
     * overlap", which the shipped page has never done — leaving the token doc and the implementation
     * stating opposite rules about the same constant, so neither could be relied on.
     */
    const val SettleMs = 380

    /**
     * How long a wait must last before it is worth painting.
     *
     * **A wait that resolves faster than this should never be seen.** The sign-in handoff and the
     * app-shell skeleton are both transient screens shown while a session resolves, and when the
     * answer is already cached that resolution takes a frame or two. Painting them anyway put a
     * whole animated splash — a rotating wordmark, a full sidebar — on screen for ~80ms and tore it
     * away again, which reads as a flicker rather than as feedback and is exactly the thing the
     * first motion rule forbids.
     *
     * 200ms because below roughly a fifth of a second a change reads as part of the same action
     * rather than as a new screen, so anything that appears and leaves inside that window has cost
     * the reader attention and told them nothing. Above it, a blank pause starts to look broken and
     * the wait is worth announcing.
     *
     * This is a delay before rendering, not a duration of motion, which is why it is not one of the
     * easing-paired constants above.
     */
    const val WaitGraceMs = 200

    /** Entering, or responding to a user action. Fast start, gentle settle. */
    const val EaseOut = "cubic-bezier(.2, .8, .2, 1)"

    /** Strong deceleration for Assist enter and exit, where motion must finish near the action. */
    const val EaseStrongOut = "cubic-bezier(.23, 1, .32, 1)"

    /** Leaving. Gentle start, quick exit, so dismissals feel decisive. */
    const val EaseIn = "cubic-bezier(.4, 0, 1, 1)"

    /** Moving between two resting states, where neither end is an entrance or an exit. */
    const val EaseInOut = "cubic-bezier(.4, 0, .2, 1)"

    /** The signature curve. One orchestrated moment only. */
    const val EaseEmphasized = "cubic-bezier(.2, 0, 0, 1)"

    /**
     * A sheet arriving from off screen. Leaves quickly, settles long.
     *
     * The one curve here taken from outside rather than derived: it is what Vaul ships for drawers
     * and what the animation playbook names for the same job. A panel that travels the whole width
     * of itself needs a curve that spends its time at the end, or it reads as sliding to a stop
     * rather than as coming to rest. Only [Modifier.sheetSurface] uses it.
     */
    const val EaseDrawer = "cubic-bezier(.32, .72, 0, 1)"

    /**
     * The same two curves again, for the Kotlin animation API.
     *
     * `Keyframes.toAnimation` and `transition { timingFunction(…) }` take four numbers; the CSS
     * property above takes a string. So six call sites re-typed the numbers by hand — the entrance
     * curve in `LRouteTransition`, `LReveal`, `LMenu` and the multiplication band, the emphasized
     * one in `GlyphMosaic` and the script wall — and a change to the scale would have reached the
     * string spelling and left those six behind, drifting the product's most visible motion away
     * from everything transitioning beside it.
     *
     * Declared next to the strings they mirror, because two spellings of one curve only stay equal
     * if they are read together. `MotionTokenTest` fails if they stop matching.
     */
    val EaseOutTiming: AnimationTimingFunction = AnimationTimingFunction.cubicBezier(0.2, 0.8, 0.2, 1.0)

    /** [EaseEmphasized] for the Kotlin animation API. See [EaseOutTiming]. */
    val EaseEmphasizedTiming: AnimationTimingFunction = AnimationTimingFunction.cubicBezier(0.2, 0.0, 0.0, 1.0)

    /**
     * How far a button compresses when pressed.
     *
     * Always this value. Below 0.95 the press reads as a flinch rather than a button taking the
     * weight of a finger, and the difference between 0.96 and 0.94 is the difference between
     * "responsive" and "cheap".
     */
    const val PressScale = 0.96

    /** How far a raised surface lifts under the cursor. Small enough to feel like light, not lift. */
    const val HoverLiftPx = 2

    // ---- Surfaces that appear ------------------------------------------------------------------
    //
    // Menus, popovers, modals and sheets had no entrance at all: they were in the DOM on one frame
    // and not on the previous one. That is not restraint, it is an omission — the account menu
    // materialising fully formed is the one moment in the product that reads as a web page rather
    // than as an instrument, because nothing connects the panel to the control that summoned it.
    //
    // What follows is deliberately not a spring, and deliberately not long. The rule this product
    // works under is "quiet beats expressive" and "it has to survive eight hours", so the money is
    // spent on three things a bounce cannot buy:
    //
    //   1. **Origin.** A surface grows from the control that opened it, so the eye never has to
    //      find it. That is `transform-origin`, and it is why these tokens are a scale rather than
    //      only a fade.
    //   2. **Asymmetry.** Opening is a considered 180ms; closing is 120ms. A dismissal that takes
    //      as long as an appearance feels like the interface arguing with you.
    //   3. **Distance discipline.** 6px and 0.97, not 20px and 0.85. At this size the movement is
    //      below the threshold at which it reads as "an animation" and above the one at which it
    //      reads as nothing — which is exactly the register an expensive tool works in.

    /**
     * The scale a popover starts at.
     *
     * 0.97, not 0.9. The panel must read as arriving, not as inflating: below about 0.95 the text
     * inside it visibly resamples during the transition, and blurry type for 180ms is the single
     * cheapest-looking thing an interface can do.
     */
    const val PopoverEnterScale = 0.97

    /** How far a popover rises into place. Small enough to be felt rather than watched. */
    const val PopoverTravelPx = 6

    /**
     * How far a tooltip travels, which is half a popover's distance and deliberately so.
     *
     * Rule 1 is that duration follows distance; the corollary is that distance follows size. A
     * tooltip is a line of text beside a control, and a menu is a panel — moving the two the same
     * distance makes the smaller one look like it is being thrown. This was four hand-typed `3px`
     * literals sitting beside a tokenised start scale, so the pair read as half-finished rather
     * than as a decision. It is a decision.
     */
    const val TooltipTravelPx = 3

    /**
     * The scale an in-place glyph swap starts from.
     *
     * For the two places one icon replaces another without moving — a checkbox settling into its
     * tick, a copy button confirming. The copy glyph entered from `scale(.72)`, a 28% jump that is
     * a pop rather than a settle and disagreed with the checkbox beside it for no stated reason.
     * One number, so the two agree by construction.
     */
    const val GlyphEnterScale = 0.92

    /** Opening a surface that covers content: considered, because it is taking the screen. */
    const val OverlayEnterMs = SlowMs

    /** Closing it: quicker than opening, so dismissal feels like a decision rather than a wait. */
    const val OverlayExitMs = FastMs

    /**
     * Emphasized decelerate — nearly all of the movement happens in the first third.
     *
     * For surfaces that appear. It differs from [EaseOut] in where it spends its time, not in how
     * long it takes: the panel is essentially in place before the eye has finished moving to it,
     * and the remainder is a settle rather than a slide. This is the curve that separates a menu
     * that feels instant-but-connected from one that feels slow.
     */
    const val EaseEntrance = "cubic-bezier(.05, .7, .1, 1)"

    // There is no per-item stagger token for the items inside an appearing surface, and the one
    // that was here — `StaggerStepMs = 22` — is gone rather than wired up. Its doc described a
    // shipped effect in detail ("a six-item menu finishes 110ms after the panel does, which the eye
    // reads as depth") and nothing in either app had ever called it: `LMenu` renders its items with
    // no stagger at all. A token that documents behaviour the product does not have is worse than
    // no token, because the next reader plans around it. The menus it described are also the wrong
    // place for one — the account menu is opened many times a day, and the frequency rule says a
    // surface reached that often gets less motion, not more.

    /** Paint-only properties — cheap to animate at any size. */
    val ColorProperties = arrayOf("color", "background-color", "border-color", "outline-color", "box-shadow")

    /** Compositor-only properties — safe to animate on large surfaces. */
    val SurfaceProperties = arrayOf("opacity", "transform")
}

/**
 * Animate [properties] as one consistent transition.
 *
 * Prefer [transitionColors] or [transitionSurface]; use this directly only when a component needs a
 * property the two helpers do not cover.
 */
fun Modifier.transition(
    vararg properties: String,
    durationMs: Int = Motion.FastMs,
    easing: String = Motion.EaseOut,
    delayMs: Int = 0,
): Modifier = styleModifier {
    val delay = if (delayMs == 0) "" else " ${delayMs}ms"
    property(
        "transition",
        properties.joinToString(", ") { name -> "$name ${durationMs}ms $easing$delay" },
    )
}

/** Hover, focus and selection colour changes. The default for interactive surfaces. */
fun Modifier.transitionColors(
    durationMs: Int = Motion.FastMs,
    easing: String = Motion.EaseOut,
): Modifier = transition(*Motion.ColorProperties, durationMs = durationMs, easing = easing)

/** Opacity and transform. Use for anything that moves, scales or fades. */
fun Modifier.transitionSurface(
    durationMs: Int = Motion.StandardMs,
    easing: String = Motion.EaseOut,
): Modifier = transition(*Motion.SurfaceProperties, durationMs = durationMs, easing = easing)

/**
 * Opt a surface out of animation entirely.
 *
 * Used by the editor's key list: selection has to feel like the caret moved, not like the UI is
 * catching up with the keyboard.
 */
fun Modifier.noTransition(): Modifier = styleModifier { property("transition", "none") }

/**
 * Present to assistive technology, absent from the page.
 *
 * The clip-rect technique rather than `display: none`, `visibility: hidden` or `opacity: 0` — all
 * three remove the element from the accessibility tree as well, which defeats the entire purpose.
 * A negative text-indent would work but breaks in right-to-left contexts, and Lingvo's own product
 * is about languages that read the other way.
 */
val LVisuallyHiddenStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("width", "1px")
        property("height", "1px")
        property("margin", "-1px")
        property("padding", "0")
        property("overflow", "hidden")
        property("clip", "rect(0, 0, 0, 0)")
        property("white-space", "nowrap")
        property("border", "0")
    }
}

fun Modifier.tabularNumbers(): Modifier = styleModifier {
    property("font-variant-numeric", "tabular-nums")
}

fun Modifier.displayFont(): Modifier = fontFamily(*FontStack.Display)

fun Modifier.monoFont(): Modifier = fontFamily(*FontStack.Mono)

/**
 * The press. One definition, so every control in the product compresses by the same amount.
 *
 * Scale rather than the `translateY(1px)` this replaces: a nudge downward is ambiguous next to a
 * hover lift, whereas compression reads unmistakably as the surface taking pressure. Paired with
 * [interactiveTransition] so the release eases back out instead of snapping.
 */
fun Modifier.pressScale(): Modifier = styleModifier {
    property("transform", "scale(${Motion.PressScale})")
}

/**
 * The transition every interactive surface shares.
 *
 * Named properties only — never `all`. `all` animates properties nobody intended, most visibly
 * `width` and `height` during a resize, and it defeats the compositor by forcing layout on
 * properties that would otherwise be cheap.
 */
fun Modifier.interactiveTransition(): Modifier = styleModifier {
    property(
        "transition",
        "background-color ${Motion.FastMs}ms ${Motion.EaseOut}, " +
            "border-color ${Motion.FastMs}ms ${Motion.EaseOut}, " +
            "color ${Motion.FastMs}ms ${Motion.EaseOut}, " +
            "box-shadow ${Motion.StandardMs}ms ${Motion.EaseOut}, " +
            // The base timing is the release. The active phase overrides this with
            // [pressTransition], so compression lands quickly and recovery remains softer.
            "transform ${Motion.StandardMs}ms ${Motion.EaseOut}",
    )
}

/** Fast pressure-in timing; the base [interactiveTransition] remains the slower release. */
fun Modifier.pressTransition(): Modifier = styleModifier {
    property("transition-duration", "${Motion.FastMs}ms")
}

/**
 * Disabled was three treatments via three mechanisms before this: opacity 0.5 through `LButton`'s
 * native `:disabled` rule, opacity 0.56 written inline after `.then()` on every text-entry control,
 * and opacity 0.56 again through a `[data-disabled="true"]` rule on `LCheckbox` and `LSwitch`. One
 * opacity spent three times by three different mechanisms is not one design decision — it is three
 * components that happened to agree, until the day one of them doesn't.
 *
 * `0.56` wins here because five call sites already carried it; `LButton`'s `0.5` was the outlier.
 *
 * This is the paint only. Callers set `[data-disabled="true"]` on the control's **outermost**
 * element — the one that also contains the visible label, where there is one — rather than on the
 * input alone. Opacity composites the whole subtree as one layer, so a single rule on the ancestor
 * dims the label with the field it names instead of leaving the label at full strength while the
 * control beside it fades, which is what the inline-after-`.then()` version did.
 */
fun Modifier.disabledSurface(): Modifier = styleModifier {
    property("opacity", "0.56")
    property("cursor", "not-allowed")
}

/**
 * The wrapper [LTextField], [LTextArea] and [LSelect] each compose into.
 *
 * Exists for one rule: `[data-disabled="true"]` lives here, on the element that contains both the
 * visible `<label>` and the control it names, rather than on the control alone. `LSearchField` and
 * `LDateRange` need no equivalent — neither renders a visible label sibling, so `[data-disabled]`
 * goes directly on their own existing root style.
 */
val LFieldGroupStyle = CssStyle {
    base { Modifier.gap(Space.XS) }
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
}

/**
 * Headline wrapping.
 *
 * `balance` evens the line lengths of a heading so a six-word title does not break with one word
 * stranded on the second line. Browsers cap it at a handful of lines, which is why it belongs on
 * headings and [prettyText] belongs on paragraphs.
 */
fun Modifier.balancedText(): Modifier = styleModifier {
    property("text-wrap", "balance")
}

/** Body wrapping — prevents a single-word last line without evening out the whole paragraph. */
fun Modifier.prettyText(): Modifier = styleModifier {
    property("text-wrap", "pretty")
}

/** Where the visitor's colour-mode choice is remembered between visits. */
const val COLOR_MODE_KEY: String = "lingvo:colorMode"

/**
 * Whether the visitor ever *chose* a colour mode, as opposed to being handed one.
 *
 * `AppEntry` persists [COLOR_MODE_KEY] on first composition, unconditionally, and has since it
 * shipped. So every visitor who has ever loaded any page is stored as `"DARK"` — the deliberate
 * choosers and the people who never touched the toggle are indistinguishable, and a change of
 * default would therefore convert nobody.
 *
 * This key is written **only** by the toggle. Once it has soaked long enough to be meaningful, a
 * default change can honour a real preference and give everyone else the new one. Without it, the
 * only way to move the installed base is to silently flip people who did choose.
 */
const val COLOR_MODE_EXPLICIT_KEY: String = "lingvo:colorModeExplicit"

/** True when this visitor picked a colour mode themselves. See [COLOR_MODE_EXPLICIT_KEY]. */
fun colorModeWasChosen(): Boolean =
    runCatching { kotlinx.browser.localStorage.getItem(COLOR_MODE_EXPLICIT_KEY) == "1" }
        .getOrDefault(false)

/** Records that this visitor picked a colour mode themselves. Called only from the toggle. */
fun rememberColorModeWasChosen() {
    runCatching { kotlinx.browser.localStorage.setItem(COLOR_MODE_EXPLICIT_KEY, "1") }
}

/**
 * What a visitor gets when they have never chosen: whatever their machine is set to.
 *
 * Read in exactly one place, which is what made this a one-token diff on a line that had already
 * shipped and been screenshotted.
 */
private val DEFAULT_COLOR_MODE: ColorMode get() = ColorMode.systemPreference

@InitSilk
fun initLingvoTheme(ctx: InitSilkContext) {
    // The stored mode is only honoured when the visitor actually picked it.
    //
    // `AppEntry` persists the colour mode on every change, so `lingvo:colorMode` has a value for
    // everyone who has ever loaded a page — including people who never touched the toggle. Reading
    // it directly would mean "everybody chose dark once" and the system preference would never be
    // consulted by anyone. `lingvo:colorModeExplicit` is the flag that separates a choice from a
    // side effect, and it is written only by the toggle.
    //
    // The pre-paint script in `site/build.gradle.kts` runs this same logic before the bundle boots.
    // The two must agree exactly: if they disagree, the page paints one mode and then swaps.
    ctx.config.initialColorMode =
        (if (colorModeWasChosen()) ColorMode.loadFromLocalStorage(COLOR_MODE_KEY) else null)
            ?: DEFAULT_COLOR_MODE
    ctx.theme.palettes.light.background = SitePalettes.light.surfaceBase
    ctx.theme.palettes.light.color = SitePalettes.light.textPrimary
    ctx.theme.palettes.dark.background = SitePalettes.dark.surfaceBase
    ctx.theme.palettes.dark.color = SitePalettes.dark.textPrimary
    // **The link colour is a palette entry too, and leaving it unset shipped a second accent.**
    // `initSilkWidgetsKobweb` runs before this and sets `link` to `Colors.Blue` / `Colors.Cyan`
    // (visited: Purple / Violet) — user-agent hues that are in no palette here and fail against
    // this product's grounds. Every bare `Link` in the app painted itself in one of them: twenty
    // card titles on the settings hub sampled at exactly #0000FF, beside a rail whose active pill
    // was the real accent. One accent means the accent, and `visited` is the same colour because a
    // second hue for "you have been here" is a distinction this app never makes anywhere else.
    ctx.theme.palettes.light.link.set(
        default = SitePalettes.light.accent,
        visited = SitePalettes.light.accent,
    )
    ctx.theme.palettes.dark.link.set(
        default = SitePalettes.dark.accent,
        visited = SitePalettes.dark.accent,
    )

    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    // The shared entrance, registered once so `Modifier.enterStaggered(index)` costs a page nothing
    // but a delay. A card arriving is 6px of rise — enough to read as assembly, small enough that six
    // of them in sequence is not a wait.
    //
    // **Transform only. The fade is gone, and its absence is the point.**
    //
    // This animated `opacity: 0 -> 1` under `animation-fill-mode: both`, which is the exact failure
    // `LReveal` and `LRouteTransition` each forbid by name in their own KDoc and which this design
    // system states as a rule: a keyframe whose opening frame is transparent, held by `both`, leaves
    // its content invisible in any tab the browser has stopped painting. The animation never starts,
    // the opening frame never advances, and the reader returns to a page with holes in it. It has
    // shipped an invisible surface in this codebase before.
    //
    // The rule the two component files state and this one violated: **the base style is always the
    // finished, readable state, and a keyframe may only describe a departure from it.** If this
    // animation never runs, every card is exactly where it belongs.
    ctx.stylesheet.registerKeyframes("lingvo-enter") {
        from { Modifier.translateY(6.px) }
        to { Modifier.translateY(0.px) }
    }

    // The toast entrance used to be registered here too, as `l-toast-enter`. It is a `data-presence`
    // transition in `LToastStyle` now, for the reason that file states at length: a keyframe cannot
    // be retargeted, so dismissing a toast mid-entrance had to cancel the animation by hand and the
    // panel flicked back to its resting place on the way out. Every other appearing surface in the
    // system was already a transition; this was the exception.

    // **Which way "forward" points, as a number a `calc()` can multiply by.**
    //
    // Logical properties handle the static half of a right-to-left layout — `margin-inline-start`
    // knows which edge it means. Transforms do not: `translateX(16px)` moves right in Arabic exactly
    // as it does in English, so a switch whose thumb rests at the inline start would slide *off* its
    // own track. There is no logical `translateX`, and there is no need for one; a sign is enough.
    //
    // `--l-inline-end` does the same job for the properties that take a physical keyword and have no
    // logical spelling at all, `transform-origin` being the one this system needs.
    ctx.stylesheet.registerStyleBase(":root") {
        Modifier.styleModifier {
            property("--l-inline-direction", "1")
            property("--l-inline-start", "left")
            property("--l-inline-end", "right")
        }
    }
    ctx.stylesheet.registerStyleBase("[dir=\"rtl\"]") {
        Modifier.styleModifier {
            property("--l-inline-direction", "-1")
            property("--l-inline-start", "right")
            property("--l-inline-end", "left")
        }
    }

    // **An arrow that means "next" points the other way in Arabic.**
    //
    // Marked at the call site rather than inside the icon, because `LChevronRightIcon` says *right*
    // and sometimes that is what a caller means — a fixed orientation, a direction on a map. Only a
    // glyph standing for forward motion through the interface turns around, and it has to say so.
    ctx.stylesheet.registerStyleBase("[dir=\"rtl\"] [data-directional=\"inline\"]") {
        Modifier.styleModifier { property("transform", "scaleX(-1)") }
    }

    // Honouring prefers-reduced-motion in one place, for everything.
    //
    // A per-component opt-out gets forgotten by the next component. This removes the entrances
    // globally and leaves the content exactly where it would have landed, which is what the
    // preference actually asks for — not the same movement, faster.
    ctx.stylesheet.registerStyle("*, *::before, *::after") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("reduce"))) {
            Modifier.styleModifier {
                property("animation-duration", "0.01ms !important")
                property("animation-iteration-count", "1 !important")
                property("transition-duration", "0.01ms !important")
                // The two delays are the ones that were missing, and their absence is why several
                // components carry their own `matchMedia` guard: neutralising a duration but not a
                // delay leaves a staggered entrance holding its opening frame for the full stagger,
                // so the third line of a three-line entrance sat invisible for half a second under
                // a preference that asked for no movement at all. All four, or none of them work.
                property("animation-delay", "0.01ms !important")
                property("transition-delay", "0.01ms !important")
                property("scroll-behavior", "auto !important")
            }
        }
    }
    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .margin(0.px)
            .fontFamily(*FontStack.Body)
            .fontSize(TypeScale.Body)
            .lineHeight(TypeScale.BodyLineHeight)
            .tabularNumbers()
            .prettyText()
    }
    registerScriptFonts(ctx)
    // Every heading, once, rather than per-component and forgotten on the next one.
    ctx.stylesheet.registerStyleBase("h1, h2, h3, h4") {
        Modifier.balancedText()
    }
    ColorMode.entries.forEach { colorMode ->
        val palette = colorMode.toSitePalette()
        val shadows = colorMode.toElevationShadows()
        ctx.stylesheet.registerStyleBase(".${colorMode.cssClass}") {
            Modifier
                .setVariable(ColorTokens.SurfaceBase, palette.surfaceBase)
                .setVariable(ColorTokens.Surface, palette.surface)
                .setVariable(ColorTokens.SurfaceRaised, palette.surfaceRaised)
                .setVariable(ColorTokens.SurfaceSunken, palette.surfaceSunken)
                .setVariable(ColorTokens.Border, palette.border)
                .setVariable(ColorTokens.BorderStrong, palette.borderStrong)
                .setVariable(ColorTokens.TextPrimary, palette.textPrimary)
                .setVariable(ColorTokens.TextSecondary, palette.textSecondary)
                .setVariable(ColorTokens.TextMuted, palette.textMuted)
                .setVariable(ColorTokens.Accent, palette.accent)
                .setVariable(ColorTokens.AccentHover, palette.accentHover)
                .setVariable(ColorTokens.AccentSubtle, palette.accentSubtle)
                .setVariable(ColorTokens.AccentContrast, palette.accentContrast)
                .setVariable(ColorTokens.Success, palette.success)
                .setVariable(ColorTokens.Warning, palette.warning)
                .setVariable(ColorTokens.Danger, palette.danger)
                .setVariable(ColorTokens.Info, palette.info)
                // Today's shipped values, identical in both modes. Phase A is where a light
                // ground gets to ask for something other than 62% black.
                .setVariable(ScrimVars.Modal, Color.rgba(0, 0, 0, 0.62f))
                .setVariable(ScrimVars.Sheet, Color.rgba(0, 0, 0, 0.54f))
                // Unlike the scrims above, these genuinely differ per mode — see [Elevation].
                .setVariable(Elevation.Floating, StylePropertyValue(shadows.floating))
                .setVariable(Elevation.Sheet, StylePropertyValue(shadows.sheet))
        }
        ctx.stylesheet.registerStyleBase(".${colorMode.cssClass} body") {
            Modifier
                .backgroundColor(palette.surfaceBase)
                .color(palette.textPrimary)
                .styleModifier {
                    // The product is a wall of text in languages the reader may not speak, looked
                    // at for hours. On macOS the default subpixel rendering makes light-on-dark
                    // type look heavier than it was drawn; antialiasing keeps its weight.
                    //
                    // That argument is about light-on-dark specifically, so this belongs per mode
                    // rather than globally — antialiasing dark-on-light thins type that is already
                    // thin. Both modes keep today's value here; the light ground is free to drop
                    // it once there is a light ground worth looking at.
                    property("-webkit-font-smoothing", "antialiased")
                    property("-moz-osx-font-smoothing", "grayscale")
                }
        }
    }
    ctx.stylesheet.registerStyleBase("*, *::before, *::after") {
        Modifier.styleModifier { property("box-sizing", "border-box") }
    }
    ctx.stylesheet.registerStyleBase("button, input, textarea, select") {
        Modifier.fontFamily(*FontStack.Body)
    }
}
